// src/service/accountService.ts
import axios from "axios";
import { ISession } from "../model/session.model";
import { IJoinRequest } from "../model/join.model";
import { sessionService } from "./session.service";
import { ILoginRequest } from "../model/login.model";
import { RaspberryPi } from "../model/RaspberryPi";
import { PiService } from "./piService";
import { Config } from "../config/config";

export interface IAccount {
  _id: string;
  displayName: string;
  email: string;
  subdomain?: string;
  cameras?: Array<{
    ref: string;
    url: string;
    displayName: string;
    abstandsschwelle: number;
  }>;
  plcUrl?: string;
  raspberryPi?: RaspberryPi;  // Referenz zum Raspberry Pi
  assignedRaspberryPiRef?: string; // Referenz/Name des zugewiesenen Pis
  subscribedUntil?: Date;
  isAdmin: boolean;
  createdAt?: Date; // Hinzugefügt für Detailansicht
  updatedAt?: Date; // Hinzugefügt für Detailansicht
}

export class AccountService {
  private static client = axios;

  /**
   * Account erstellen (Registrierung)
   * @param request
   */
  static async join(request: IJoinRequest): Promise<ISession> {
    try {
      const response = await this.client.post<ISession>("/join", request);
      const session = response.data;
      sessionService.setSession(session);
      return session;
    } catch (error: any) {
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) => {
          switch (err.param) {
            case 'password':
              return 'Das Passwort muss mindestens 6 Zeichen lang sein';
            case 'email':
              if (err.msg.includes('already in use')) {
                return 'Diese E-Mail-Adresse wird bereits verwendet';
              }
              return 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
            case 'displayName':
              return 'Bitte geben Sie einen gültigen Waschstraßennamen ein';
            default:
              return err.msg || 'Ein Fehler ist aufgetreten';
          }
        });
        throw { errorMessages };
      }

      // Only log essential error information
      const errorMessage = error.response?.data?.message || error.message || 'Ein unerwarteter Fehler ist aufgetreten';
      console.error('Join error:', errorMessage);

      throw { 
        errorMessages: [errorMessage],
        technicalDetails: {
          status: error.response?.status,
          message: error.message,
          data: error.response?.data
        }
      };
    }
  }

  /**
   * Benutzer-Login
   * @param request
   */
  static async login(request: ILoginRequest): Promise<ISession> {
    try {
      const response = await this.client.post<ISession>("/login", request);
      const session = response.data;
      
      // Stellen Sie sicher, dass der Token korrekt gespeichert wird
      if (session.token) {
        localStorage.setItem('token', session.token);
      }
      
      // Session im Service speichern, um sicherzustellen, dass der Login-Status gesetzt ist
      sessionService.setSession(session);
      
      // Versuch die Session zu validieren
      try {
        const validatedSession = await sessionService.validate();
        
        if (validatedSession.loggedIn) {
          return validatedSession;
        } else {
          // Verwende die direkte Antwort, wenn die Validierung fehlschlägt
          return session;
        }
      } catch (validationError) {
        // Wenn die Validierung fehlschlägt, aber wir eine gültige Login-Antwort haben,
        // verwenden wir die direkte Antwort
        return session;
      }
    } catch (error: any) {
      console.error("Login failed:", error.message || error);
      throw error;
    }
  }

  /**
   * Benutzer abmelden (Logout)
   */
  static async signout(): Promise<ISession> {
    try {
      const response = await this.client.post<ISession>("/signout");
      const session = response.data;
      sessionService.setSession(session);
      return session;
    } catch (error: any) {
      console.error("Signout failed:", error.message || error);
      throw error;
    }
  }

  /**
   * Alle Accounts abrufen (Admin-Funktion)
   */
  static async getAccounts(): Promise<IAccount[]> {
    try {
      const response = await this.client.get<IAccount[]>("/accounts");
      
      // Log information about Pi assignments in accounts
      if (Array.isArray(response.data)) {
        response.data.forEach(account => {
          console.log(`[AccountService] Account ${account.displayName} data:`, {
            id: account._id,
            hasAssignedPiRef: !!account.assignedRaspberryPiRef,
            hasPiObject: !!account.raspberryPi,
            hasSubdomain: !!account.subdomain,
            // These fields might be present in accounts with Pi assignments
            hasWireguardKey: !!(account as any).wireguardPresharedKey
          });
        });
      }
      
      return response.data;
    } catch (error: any) {
      console.error("Failed to load accounts:", error.message || error);
      throw error;
    }
  }

  /**
   * Eigenen Account abrufen
   */
  static async getMyAccount(): Promise<IAccount> {
    try {
      const response = await this.client.get<IAccount>("/my-account");
      
      // Log Pi assignment data
      const account = response.data;
      console.log(`[AccountService] My account data (${account.displayName}):`, {
        id: account._id,
        hasAssignedPiRef: !!account.assignedRaspberryPiRef,
        hasPiObject: !!account.raspberryPi,
        hasSubdomain: !!account.subdomain,
        // These fields might be present in accounts with Pi assignments
        hasWireguardKey: !!(account as any).wireguardPresharedKey
      });
      
      return account;
    } catch (error: any) {
      console.error("Failed to load account:", error.message || error);
      throw error;
    }
  }

  /**
   * Passwort ändern
   * @param userId - ID des Nutzers
   * @param newPassword - Neues Passwort
   */
  static async changePassword(userId: string, newPassword: string): Promise<void> {
    try {
      await this.client.patch(`/accounts/${userId}/password`, { password: newPassword });
    } catch (error: any) {
      console.error("Password change failed:", error.message || error);
      throw error;
    }
  }

  /**
   * Account aktualisieren (Admin-Funktion)
   */
  static async updateAccount(accountId: string, accountData: Partial<IAccount>): Promise<IAccount> {
    try {
      const response = await this.client.patch<IAccount>(`/accounts/${accountId}`, accountData);
      return response.data;
    } catch (error: any) {
      console.error(`Failed to update account ${accountId}:`, error.message || error);
      throw error;
    }
  }

  /**
   * Account löschen (Admin-Funktion)
   */
  static async deleteAccount(accountId: string): Promise<void> {
    try {
      await this.client.delete(`/accounts/${accountId}`);
    } catch (error: any) {
      console.error(`Failed to delete account ${accountId}:`, error.message || error);
      throw error;
    }
  }

  /**
   * Raspberry Pi einem Account zuweisen (Admin-Funktion)
   * @param accountId ID des Accounts
   * @param raspberryPiId ID des Raspberry Pi
   */
  static async assignRaspberryPi(accountId: string, raspberryPiId: string): Promise<void> {
    try {
      console.log(`[AccountService] Attempting to assign Pi ${raspberryPiId} to account ${accountId}`);
      
      // Prüfen, ob der Pi bereits zugewiesen ist
      try {
        const pi = await PiService.getPiById(raspberryPiId);
        console.log(`[AccountService] Current Pi details:`, {
          id: pi._id,
          name: pi.name,
          serialNumber: pi.serialNumber,
          currentOwnerId: pi.ownerId || 'none',
          status: pi.status
        });
        
        if (pi.ownerId) {
          console.warn(`[AccountService] Pi is already assigned to owner: ${pi.ownerId}. Will try to reassign.`);
        }
      } catch (e) {
        // Silence this error
        console.error('[AccountService] Error checking Pi current status:', e);
      }
      
      // Statt PUT /accounts/:id/assign-pi verwenden wir die korrekte PiService Methode
      console.log(`[AccountService] Calling PiService.assignToOwner(${raspberryPiId}, ${accountId})`);
      const updatedPi = await PiService.assignToOwner(raspberryPiId, accountId);
      
      console.log(`[AccountService] Assignment result:`, {
        piId: updatedPi._id,
        piName: updatedPi.name,
        newOwnerId: updatedPi.ownerId,
        expectedOwnerId: accountId,
        match: updatedPi.ownerId === accountId
      });
      
      // NOTE: The API might not return the ownerId in the response
      // In that case, we'll assume success if we got a 200 OK response
      if (updatedPi.ownerId !== undefined && updatedPi.ownerId !== accountId) {
        console.error(`[AccountService] Pi assignment failed - owner mismatch:`, {
          updatedPiOwnerId: updatedPi.ownerId,
          expectedAccountId: accountId
        });
        throw new Error(`Pi assignment failed: owner ID mismatch`);
      }
      
      console.log(`[AccountService] Pi assignment successful`);
      
      // Verify assignment by fetching Pi details directly
      try {
        // Wait a moment for backend to process
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const verifiedPi = await PiService.getPiById(raspberryPiId);
        console.log('[AccountService] Verification check:', {
          ownerId: verifiedPi.ownerId,
          expectedId: accountId,
          match: verifiedPi.ownerId === accountId
        });
        
        if (verifiedPi.ownerId !== accountId) {
          console.warn('[AccountService] Verification warning: Pi may not be correctly assigned');
        }
      } catch (verifyError) {
        console.warn('[AccountService] Could not verify assignment:', verifyError);
      }
      
    } catch (error: any) {
      console.error(`Pi assignment failed:`, error.message || error);
      throw error;
    }
  }

  /**
   * Zuweisung eines Raspberry Pi von einem Account entfernen (Admin-Funktion)
   * @param accountId ID des Accounts
   */
  static async unassignRaspberryPi(accountId: string): Promise<void> {
    try {
      console.log(`[AccountService] Starting unassignment process for account ${accountId}`);
      
      // Zuerst finden wir den Pi, der diesem Account zugewiesen ist
      const assignedPis = await PiService.getPiForOwner(accountId);
      
      if (assignedPis.length === 0) {
        console.log(`[AccountService] No Pi found assigned to account ${accountId}`);
        return;
      }
      
      // Wenn ein Pi gefunden wurde, heben wir die Zuweisung auf
      const piToUnassign = assignedPis[0];
      console.log(`[AccountService] Found Pi assigned to account: ${piToUnassign.name} (${piToUnassign._id})`);
      
      // Unassign the Pi using the improved PiService method
      await PiService.unassignFromOwner(piToUnassign._id);
      
      // Verify the unassignment was successful
      console.log(`[AccountService] Verifying Pi unassignment was successful`);
      const updatedPis = await PiService.getPiForOwner(accountId);
      
      if (updatedPis.length > 0) {
        console.warn(`[AccountService] Pi still appears to be assigned to account after unassignment`);
        
        // Don't throw an error - the backend might have a delay in updating references
        // Instead, log a warning but consider the operation successful
        console.log(`[AccountService] Unassignment operation completed with warning`);
      } else {
        console.log(`[AccountService] Unassignment successful - no Pis found for account ${accountId}`);
      }
    } catch (error: any) {
      console.error(`[AccountService] Failed to unassign Pi:`, error.message || error);
      throw error;
    }
  }
}
